import { foodOrigin } from '../lib/food-origin';
import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  MEAL_TITLE,
  formatDecimal,
  formatShortDate,
  type Meal,
} from '@jungles/shared';
import { foodNutritionLabel, displayFoodBrand, foodPortionGrams, foodPortionMl, foodSelection, unifiedFoods, type SearchFood, type FoodSearchResponse } from '../lib/food-search';
import { useApi, useDebounced } from '../lib/hooks';
import { Loader } from '../components/Chrome';
import { FoodSearchSheet } from '../components/FoodSearchSheet';
import { BarcodeIcon, SearchIcon } from '../components/icons';

/** N4 · Поиск продукта — шторка на весь экран (SPEC §5). */

function Highlighted({ text, query }: { text: string; query: string }) {
  const needle = query.trim().toLowerCase();
  if (!needle) return <>{text}</>;
  const index = text.toLowerCase().indexOf(needle);
  if (index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <mark>{text.slice(index, index + needle.length)}</mark>
      {text.slice(index + needle.length)}
    </>
  );
}

export function FoodSearchScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const origin = foodOrigin(location.state);

  const meal = (['breakfast','lunch','dinner','snack'].includes(params.get('meal') ?? '') ? params.get('meal') : 'snack') as Meal;
  const date = params.get('date') ?? '';
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const debounced = useDebounced(query, 600);

  const { data: response, loading, error, reload } = useApi<FoodSearchResponse>(
    `/api/foods?q=${encodeURIComponent(debounced.trim())}&offset=${offset}`,
  );
  const searching = loading || query !== debounced;
  const data = searching || error ? null : response;
  const foods = data ? unifiedFoods(data) : [];
  const close = () => location.state?.foodBackground ? navigate(-1) : navigate(origin?.path ?? `/nutrition?date=${date}`, {replace:true,state:origin ? {restoreFoodOrigin:origin} : undefined});

  const openWithGrams = (food: SearchFood) => {
    if (food.historyEntryId) {
      const selection = {...food,id:`history:${food.historyEntryId}`};
      navigate(`/nutrition/food?meal=${meal}&date=${date}&foodId=${encodeURIComponent(selection.id)}`, {state:{foodOrigin:origin,food:selection}});
      return;
    }
    if (food.external && food.source !== 'fatsecret') {
      navigate(`/nutrition/food?meal=${meal}&date=${date}`, { state: { foodOrigin: origin, prefill: foodSelection(food).prefill, catalogSelection: food.kcal100 !== null && food.p100 !== null && food.f100 !== null && food.c100 !== null && Boolean(food.name), portionGrams: foodPortionGrams(food) } });
      return;
    }
    navigate(`/nutrition/food?meal=${meal}&date=${date}&foodId=${food.id}`, { state: { foodOrigin: origin, food } });
  };

  const renderRow = (food: SearchFood) => (
    <li key={food.id}>
      <button className="rn" type="button" onClick={() => openWithGrams(food)}>
        <b>
          <Highlighted text={food.name} query={debounced} />
        </b>
        {displayFoodBrand(food.brand) ? <small>{displayFoodBrand(food.brand)}</small> : null}
        {foodPortionMl(food) !== null ? <small>Порция · {formatDecimal(foodPortionMl(food)!)} мл</small> : foodPortionGrams(food) !== null ? <small>Порция · {formatDecimal(foodPortionGrams(food)!)} г</small> : null}
        <small>{foodNutritionLabel(food)}</small>
      </button>
      <button
        className="qa"
        type="button"
        onClick={() => openWithGrams(food)}
        aria-label={`Добавить «${food.name}» в ${MEAL_TITLE[meal].toLowerCase()}`}
      >
        +
      </button>
    </li>
  );

  return (
    <FoodSearchSheet onClose={close} returnLabel={`Добавить в «${MEAL_TITLE[meal]}»`}>
      {dismiss => <>
      <div className="sbar" data-sheet-header>
        <label className="srch" style={{ marginTop: 0 }}>
          <SearchIcon />
          <span className="vh">Поиск продукта</span>
          <input
            type="search"
            value={query}
            onChange={(event) => {setOffset(0);setQuery(event.target.value);}}
            placeholder="Найти продукт или блюдо"
            autoFocus
            autoComplete="off"
            maxLength={120}
          />
          {query ? (
            <button
              className="clr"
              type="button"
              onClick={() => {setOffset(0);setQuery('');}}
              aria-label="Очистить"
            >
              ×
            </button>
          ) : null}
        </label>
        <button className="cnl" type="button" onClick={dismiss}>
          Отмена
        </button>
      </div>

      <span className="ctx" style={{ marginTop: 12 }}>
        В «{MEAL_TITLE[meal]}» · {date ? formatShortDate(date) : 'сегодня'}
      </span>

      <div className="food-search-results" aria-live="polite">
      {searching ? <Loader label="Ищем…" /> : null}
      {error ? <p className="err" role="alert">{error}</p> : null}
      {data?.externalError ? <p className="err" role="alert">{data.externalError}</p> : null}
      {error || data?.externalError ? <button type="button" className="pill-btn" onClick={reload}>Повторить</button> : null}
      {debounced.trim().length < 3 ? <p className="nt">Для расширенного поиска введите от 3 символов.</p> : null}

      {data?.recent.length ? <><h2 className="food-result-heading">Мои и недавние</h2><ul className="res">{data.recent.map(renderRow)}</ul></> : null}
      {foods.some(f=>!data?.recent.some(r=>r.id===f.id)) ? <><h2 className="food-result-heading">Каталог</h2><ul className="res">{foods.filter(f=>!data?.recent.some(r=>r.id===f.id)).map(renderRow)}</ul></> : null}

      {data && (offset > 0 || data.hasMore?.recent || data.hasMore?.catalog) ? <nav className="food-pagination" aria-label="Страницы продуктов">
        <button type="button" className="pill-btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0,offset-12))}>Назад</button>
        <button type="button" className="pill-btn" aria-label="Следующие продукты" disabled={!data.hasMore?.recent && !data.hasMore?.catalog} onClick={() => setOffset(offset+12)}>Далее</button>
      </nav> : null}

      {data && !data.externalError && foods.length === 0 && !searching ? (
        <p className="empty">Ничего не нашли</p>
      ) : null}

      </div>
      <div className="nf">
        <div className="nt">Не нашли нужное?</div>
        <div className="row2">
          <button
            className="pill-btn"
            type="button"
            onClick={() => navigate(`/nutrition/food?meal=${meal}&date=${date}`, { state: { foodOrigin: origin } })}
          >
            <img src="/assets/ui/tab-food.webp" alt="" />
            Вручную
          </button>
          <button
            className="pill-btn"
            type="button"
            onClick={() => navigate(`/nutrition/scan?meal=${meal}&date=${date}`, { state: { foodOrigin: origin } })}
          >
            <BarcodeIcon />
            Сканировать
          </button>
        </div>
      </div>
      </>}
    </FoodSearchSheet>
  );
}
